import {
  CreditCard,
  Gauge,
  Inbox,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  Users,
  X,
} from "./icons";
import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { endpoints } from "../lib/api";
import { authClient } from "../lib/auth";
import { useLoad } from "../hooks";
import { PRODUCT_NAME } from "../product.generated";
import { Button, Skeleton } from "./ui";

const links = [
  ["/dashboard", "Overview", Gauge],
  ["/accounts", "Mailboxes", Inbox],
  ["/recent-spam", "Recent spam", ShieldCheck],
  ["/workspace", "Workspace", Users],
  ["/billing", "Billing", CreditCard],
  ["/settings/security", "Settings", Settings],
] as const;

export function AppShell() {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const navigate = useNavigate();
  const session = useLoad(endpoints.session, []);
  if (session.loading)
    return (
      <div className="center-screen">
        <Skeleton className="h-12 w-52" />
      </div>
    );
  if (session.error) return <NavigateToLogin />;
  const user = session.data!.user;
  const logout = async () => {
    setSigningOut(true);
    const result = await authClient.signOut();
    if (result.error) {
      setSigningOut(false);
      return;
    }
    navigate("/", { replace: true });
  };
  return (
    <div className="app-frame">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <button
        className="mobile-menu"
        aria-label="Open navigation"
        aria-expanded={open}
        aria-controls="product-navigation"
        onClick={() => setOpen(true)}
      >
        <Menu />
      </button>
      <aside
        id="product-navigation"
        className={`sidebar ${open ? "sidebar-open" : ""}`}
      >
        <div className="brand-row">
          <Logo />
          <button
            className="sidebar-close"
            aria-label="Close navigation"
            onClick={() => setOpen(false)}
          >
            <X />
          </button>
        </div>
        <nav aria-label="Product navigation">
          {links.map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `nav-link ${isActive ? "nav-link-active" : ""}`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-legal" aria-label="Legal information">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
        <div className="sidebar-foot">
          <div className="avatar" aria-hidden="true">
            {user.email[0]?.toUpperCase()}
          </div>
          <div className="user-meta">
            <strong>{user.name || "FuckSpam user"}</strong>
            <span>{user.email}</span>
          </div>
          <Button
            variant="ghost"
            aria-label="Sign out"
            disabled={signingOut}
            onClick={() => void logout()}
          >
            <LogOut size={18} />
          </Button>
        </div>
      </aside>
      {open && (
        <button
          className="backdrop"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <main id="main-content" className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

export function Logo() {
  return (
    <div className="logo">
      <span className="logo-mark">
        <ShieldCheck size={18} />
      </span>
      <span>{PRODUCT_NAME}</span>
    </div>
  );
}

function NavigateToLogin() {
  const next = window.location.pathname + window.location.search;
  window.location.replace(`/login?${new URLSearchParams({ next })}`);
  return <div className="center-screen">Redirecting…</div>;
}
